import DownloadIcon from "@mui/icons-material/Download";
import { Button } from "@mui/material";
import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "../../components/spinner";
import {
  handlesOwnKeys,
  isSpaceActivatedTarget,
} from "../../utils/keyboardTarget";

const ReactPlayer = lazy(() => import("react-player"));

/** Server-sent error (localized downstream via the error bundle). */
type ServerError = { title: string; description: string };

type FilePreviewProp = {
  filePreviewUrl?: URL;
  fileDownloadUrl?: string;
  fileUrl?: URL;
  mimeType?: string;
  objectUid: string;
  fileName: string;
  transferRecipientId: string;
  /**
   * Optional auth refresh awaited when a preview request returns 401. Provided
   * in the share-link context (re-resolves the guest cookie, rejecting on
   * failure); absent for transfers.
   */
  refreshAuth?: () => Promise<void>;
  /**
   * Optional bearer-token supplier. When present, its token is sent as an
   * `Authorization` header on preview fetches — needed for the published-space
   * guest, whose cookie is a blocked third-party cookie off the API host.
   */
  getAuthToken?: () => string | null | undefined;
  /** Reports an image preview's intrinsic size on load, so the frame can hug it. */
  onImageLoad?: (width: number, height: number) => void;
  /** Fires when the preview is actually displayed (the frame should appear). */
  onLoaded?: () => void;
  /** Fires when the inline "no preview" fallback card is shown (no frame). */
  onFallback?: () => void;
  /**
   * False while this preview is a hidden neighbor in the overlay's prev/next
   * hand-off. Media only plays — and only listens for the Space key — while it
   * is the file actually on screen. Defaults to true.
   */
  active?: boolean;
  /**
   * Fires when the preview failed with a server error worth surfacing in the
   * error dialog: a 429 (rate limit), or a 401 whose silent token refresh
   * failed. Carries the server's strings (localized downstream via the bundle).
   */
  onServerError?: (error: ServerError) => void;
};

type PreviewFallbackProp = {
  fileName: string;
  fileDownloadUrl?: string;
  refreshAuth?: () => Promise<void>;
  fileUrl?: URL;
};

/** What a successfully-fetched preview blob should be rendered as. */
type PreviewContentKind = "image" | "pdf" | "text";

type ResourceState =
  | { phase: "loading" }
  | { phase: "ready"; objectUrl: string; contentKind: PreviewContentKind }
  | { phase: "fallback" }
  | ({ phase: "error" } & ServerError);

/** Reads an `ApiException` body ({ error: { message, details } }) off a Response. */
async function readApiError(res: Response): Promise<ServerError> {
  const body = await res.json().catch(() => null);
  const serverError = body?.error;
  const details = serverError?.details;
  return {
    title: serverError?.message ?? "",
    description: Array.isArray(details) ? details.join(" ") : details ?? "",
  };
}

/** Maps a response content-type to how it should be rendered (null = can't). */
function contentKindForType(type: string): PreviewContentKind | null {
  if (type.startsWith("image/")) return "image";
  if (type.endsWith("/pdf")) return "pdf";
  if (type.startsWith("text/")) return "text";
  return null;
}

type LoadResult =
  | { status: "ok"; objectUrl: string; contentKind: PreviewContentKind }
  | { status: "rateLimited"; error: ServerError }
  | { status: "authError"; error: ServerError }
  | { status: "error" };

/**
 * Fetches a preview resource as a blob so the HTTP status can be inspected
 * (impossible with a bare `<img>`/`<iframe>`, which would otherwise display a
 * 429/401 error body *inside the frame*). Handles auth recovery and classifies
 * the outcome:
 *  - 401: silently refresh the guest token and retry once; if the refresh
 *    itself fails, report `authError` so the caller shows the dialog.
 *  - 429: report `rateLimited` with the server's message for the dialog.
 *  - empty 200 / unknown content-type / network error: `error` (→ fallback).
 *
 * The renderer is chosen from the *response* content-type, so the same file is
 * shown correctly whether the endpoint returns a rendered image (transfers) or
 * the raw bytes (share-links serve CSV/text inline, PDFs as application/pdf).
 */
async function loadPreviewResource(
  url: string,
  refreshAuth?: () => Promise<void>,
  getAuthToken?: () => string | null | undefined
): Promise<LoadResult> {
  let didRefresh = false;
  for (;;) {
    let res: Response;
    try {
      // Default cache (not `no-store`): the endpoints send `Cache-Control:
      // no-cache` + an ETag, so reopening a preview revalidates (cheap 304)
      // instead of re-downloading the bytes. Error responses (401/429) are never
      // cached, so the refresh-and-retry below still reaches the network.
      const token = getAuthToken?.();
      res = await fetch(url, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
    } catch {
      return { status: "error" };
    }

    if (res.ok) {
      const blob = await res.blob();
      // The preview endpoint answers 200 with an empty body for files it can't
      // render — treat that (and any unrenderable content-type) as a fallback.
      if (blob.size === 0) return { status: "error" };
      const contentKind = contentKindForType(blob.type);
      if (!contentKind) return { status: "error" };
      // Re-type text as text/plain so the iframe renders it inline (a raw
      // text/csv type can make the browser offer a download instead).
      const displayBlob =
        contentKind === "text"
          ? new Blob([blob], { type: "text/plain;charset=utf-8" })
          : blob;
      return {
        status: "ok",
        objectUrl: URL.createObjectURL(displayBlob),
        contentKind,
      };
    }

    if (res.status === 429) {
      return { status: "rateLimited", error: await readApiError(res) };
    }

    if (res.status === 401) {
      const error = await readApiError(res);
      // Cookie expired: refresh once, then retry the original request. If the
      // refresh fails (e.g. the share-link itself expired), surface the dialog.
      if (refreshAuth && !didRefresh) {
        try {
          await refreshAuth();
        } catch {
          return { status: "authError", error };
        }
        didRefresh = true;
        continue;
      }
      return { status: "authError", error };
    }

    return { status: "error" };
  }
}

/**
 * Loads a (non-streaming) preview resource into an object URL, reporting its
 * lifecycle as a {@link ResourceState}. Revokes the object URL on cleanup.
 */
function usePreviewResource(
  url: string | undefined,
  refreshAuth?: () => Promise<void>,
  getAuthToken?: () => string | null | undefined
): ResourceState {
  const [state, setState] = useState<ResourceState>({ phase: "loading" });

  useEffect(() => {
    if (!url) {
      setState({ phase: "fallback" });
      return;
    }
    let cancelled = false;
    let createdUrl: string | undefined;
    setState({ phase: "loading" });
    void (async () => {
      const result = await loadPreviewResource(url, refreshAuth, getAuthToken);
      if (cancelled) {
        if (result.status === "ok") URL.revokeObjectURL(result.objectUrl);
        return;
      }
      if (result.status === "ok") {
        createdUrl = result.objectUrl;
        setState({
          phase: "ready",
          objectUrl: result.objectUrl,
          contentKind: result.contentKind,
        });
      } else if (
        result.status === "rateLimited" ||
        result.status === "authError"
      ) {
        setState({ phase: "error", ...result.error });
      } else {
        setState({ phase: "fallback" });
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [url, refreshAuth, getAuthToken]);

  return state;
}

export default function FilePreview(filePreviewProp: FilePreviewProp) {
  const mime = filePreviewProp.mimeType ?? "";
  // Stream what can be streamed (media via the player, PDFs via the iframe);
  // buffer the rest (images, text) so the renderer can be chosen from the
  // response content-type and small files coerced to display inline.
  if (mime.startsWith("video") || mime.startsWith("audio")) {
    return <StreamPreview {...filePreviewProp} />;
  }
  if (mime.endsWith("pdf")) {
    return <PdfPreview {...filePreviewProp} />;
  }
  return <BlobPreview {...filePreviewProp} />;
}

/**
 * Buffered preview (images and text). Fetches the resource as a blob so 401/429
 * are handled before anything renders, then renders it per the *response*
 * content-type (so a server-rendered image of a non-image file still shows as
 * an image, and CSV/text is coerced to display inline). PDFs are streamed via
 * {@link PdfPreview} instead; the `pdf` case here is only a safety net for a
 * file whose declared MIME isn't PDF but whose bytes are. Reports its phase up
 * so the overlay can frame the preview, show the fallback card, or the dialog.
 */
function BlobPreview(props: FilePreviewProp) {
  const resource = usePreviewResource(
    props.filePreviewUrl?.href,
    props.refreshAuth,
    props.getAuthToken
  );

  // Report each phase transition exactly once. (The parent callbacks only set
  // state, so they're intentionally excluded from the deps.)
  const lastReported = useRef<ResourceState["phase"] | null>(null);
  useEffect(() => {
    if (lastReported.current === resource.phase) return;
    lastReported.current = resource.phase;
    if (resource.phase === "ready") props.onLoaded?.();
    else if (resource.phase === "fallback") props.onFallback?.();
    else if (resource.phase === "error")
      props.onServerError?.({
        title: resource.title,
        description: resource.description,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource]);

  if (resource.phase === "loading") return <Spinner size={80} />;
  // The parent replaces the whole overlay with the dialog on error.
  if (resource.phase === "error") return null;
  if (resource.phase === "fallback") {
    return (
      <PreviewFallbackUI
        fileName={props.fileName}
        fileDownloadUrl={props.fileDownloadUrl}
        refreshAuth={props.refreshAuth}
        fileUrl={props.fileUrl}
      />
    );
  }

  switch (resource.contentKind) {
    case "pdf":
      return (
        <iframe
          title={props.fileName}
          // Fit to WIDTH so the page never overflows horizontally (FitH only
          // ever scrolls vertically); toolbar/nav panes hidden.
          src={`${resource.objectUrl}#toolbar=0&navpanes=0&view=FitH`}
          className="h-full w-full"
        />
      );
    case "text":
      // Explicit size (the dark frame hugs it) — a readable document box.
      return (
        <iframe
          title={props.fileName}
          src={resource.objectUrl}
          className="h-[85vh] w-[min(90vw,820px)] bg-white"
        />
      );
    default:
      return (
        <img
          // max-* (not h-full/w-full): the image renders at its natural size,
          // only shrinking to fit the frame — so it never upscales.
          className="max-h-full max-w-full"
          src={resource.objectUrl}
          alt={props.fileName}
          decoding="async"
          draggable={false}
          onLoad={(e) => {
            const img = e.currentTarget;
            props.onImageLoad?.(img.naturalWidth, img.naturalHeight);
          }}
        />
      );
  }
}

type ProbeOutcome = "ok" | "refreshed" | "fallback" | { server: ServerError };

/**
 * Checks a preview URL's HTTP status *without* downloading the body (so it can
 * gate a streamed render — PDF iframe, media player — that would otherwise show
 * a 401/429 error body in-frame). On 401 it refreshes the token once and
 * re-checks. Outcomes:
 *  - `ok`        — reachable (render directly).
 *  - `refreshed` — reachable only after a token refresh (caller should (re)load
 *    with the now-fresh cookie).
 *  - `{ server }`— 429, or 401 whose refresh failed → show the dialog.
 *  - `fallback`  — network/other error → fallback card.
 */
async function probePreviewStatus(
  url: string,
  refreshAuth?: () => Promise<void>,
  getAuthToken?: () => string | null | undefined
): Promise<ProbeOutcome> {
  let didRefresh = false;
  for (;;) {
    let res: Response;
    try {
      const token = getAuthToken?.();
      res = await fetch(url, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
    } catch {
      return "fallback";
    }
    if (res.ok) {
      // Status is all we need; don't pull the body — the iframe/player streams it.
      void res.body?.cancel();
      return didRefresh ? "refreshed" : "ok";
    }
    if (res.status === 429) return { server: await readApiError(res) };
    if (res.status === 401) {
      const server = await readApiError(res);
      if (refreshAuth && !didRefresh) {
        try {
          await refreshAuth();
        } catch {
          return { server };
        }
        didRefresh = true;
        continue;
      }
      return { server };
    }
    return "fallback";
  }
}

/**
 * PDF preview. Probes the status first (so a 401/429 surfaces as a refresh or
 * the error dialog rather than an error body shown inside the frame), then
 * streams the PDF straight into an iframe so the viewer can show the first page
 * while the rest downloads — instead of buffering the whole file first.
 */
function PdfPreview(props: FilePreviewProp) {
  const url = props.filePreviewUrl?.href;
  const [state, setState] = useState<
    | { phase: "loading" }
    | { phase: "ready" }
    | { phase: "fallback" }
    | ({ phase: "error" } & ServerError)
  >({ phase: "loading" });

  useEffect(() => {
    if (!url) {
      setState({ phase: "fallback" });
      return;
    }
    let cancelled = false;
    setState({ phase: "loading" });
    void (async () => {
      const outcome = await probePreviewStatus(
        url,
        props.refreshAuth,
        props.getAuthToken
      );
      if (cancelled) return;
      if (outcome === "ok" || outcome === "refreshed") {
        setState({ phase: "ready" });
      } else if (outcome === "fallback") {
        setState({ phase: "fallback" });
      } else {
        setState({ phase: "error", ...outcome.server });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url, props.refreshAuth, props.getAuthToken]);

  const lastReported = useRef<string | null>(null);
  useEffect(() => {
    if (lastReported.current === state.phase) return;
    lastReported.current = state.phase;
    if (state.phase === "ready") props.onLoaded?.();
    else if (state.phase === "fallback") props.onFallback?.();
    else if (state.phase === "error")
      props.onServerError?.({
        title: state.title,
        description: state.description,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  if (state.phase === "loading") return <Spinner size={80} />;
  if (state.phase === "error") return null;
  if (state.phase === "fallback") {
    return (
      <PreviewFallbackUI
        fileName={props.fileName}
        fileDownloadUrl={props.fileDownloadUrl}
        refreshAuth={props.refreshAuth}
        fileUrl={props.fileUrl}
      />
    );
  }
  return (
    <iframe
      title={props.fileName}
      // Fit to WIDTH so the page never overflows horizontally (FitH only ever
      // scrolls vertically); toolbar/nav panes hidden.
      src={`${url}#toolbar=0&navpanes=0&view=FitH`}
      className="h-full w-full"
    />
  );
}

/**
 * A rejected `play()` — the browser's autoplay policy (`NotAllowedError`) or a
 * play cut short by a pause / reload (`AbortError`) — surfaces through
 * ReactPlayer's `onError`. Neither means the file failed to load, so they must
 * not be mistaken for a broken preview.
 */
const isPlaybackRejection = (error: unknown): boolean => {
  const name = (error as { name?: unknown } | null)?.name;
  return name === "NotAllowedError" || name === "AbortError";
};

/**
 * Streaming preview (video / audio) via ReactPlayer against the file URL
 * directly. On a load error the status is probed to recover from an expired
 * token (refresh + reload) or surface a 429, falling back otherwise.
 *
 * Playback starts on its own: opening a preview is a deliberate act, so the
 * user shouldn't have to hit play as well. Browsers that refuse to autoplay
 * with sound simply leave it paused (the rejected `play()` is recognised and
 * swallowed) with the controls ready. Space toggles play/pause from anywhere in
 * the overlay, mirroring every other player.
 */
function StreamPreview(props: FilePreviewProp) {
  const url = props.filePreviewUrl?.href;
  const [playerKey, setPlayerKey] = useState(0);
  const refreshedRef = useRef(false);
  const isAudio = props.mimeType?.startsWith("audio");
  const active = props.active ?? true;
  // Autoplay on open; kept in sync with the native controls via onPlay/onPause,
  // so the Space key always flips the state the user can actually see.
  const [playing, setPlaying] = useState(true);

  // Space toggles playback while this file is the one on screen. The native
  // player already handles Space when it has focus, so let it (a second toggle
  // here would cancel it out); the same goes for any focused text field, and for
  // a focused button/link (the overlay's close and prev/next), where Space's
  // default action *is* the press we would otherwise swallow.
  useEffect(() => {
    if (!active) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== " " && event.code !== "Space") return;
      if (
        handlesOwnKeys(event.target) ||
        isSpaceActivatedTarget(event.target)
      ) {
        return;
      }
      // Space would otherwise scroll the page behind the overlay.
      event.preventDefault();
      setPlaying((current) => !current);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [active]);

  const onError = async (error: unknown) => {
    // Autoplay refused (or a play interrupted): settle into paused and leave
    // the loaded preview alone.
    if (isPlaybackRejection(error)) {
      setPlaying(false);
      return;
    }
    if (!url) {
      props.onFallback?.();
      return;
    }
    // Don't refresh again once we already have — a persistent failure then
    // surfaces (dialog/fallback) instead of looping reload.
    const outcome = await probePreviewStatus(
      url,
      refreshedRef.current ? undefined : props.refreshAuth,
      props.getAuthToken
    );
    if (outcome === "refreshed") {
      refreshedRef.current = true;
      setPlayerKey((k) => k + 1);
    } else if (outcome === "ok" || outcome === "fallback") {
      // `ok` means the file is reachable but the player still errored (e.g.
      // codec) — nothing a reload fixes, so fall back.
      props.onFallback?.();
    } else {
      props.onServerError?.(outcome.server);
    }
  };

  return (
    <Suspense fallback={<Spinner size={80} />}>
      <ReactPlayer
        key={playerKey}
        url={url}
        stopOnUnmount
        controls
        // A hidden neighbor stays silent until the hand-off promotes it, and
        // the outgoing file stops the moment it is displaced.
        playing={playing && active}
        // iOS plays inline instead of hijacking the screen with its own player.
        playsinline
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onReady={() => props.onLoaded?.()}
        onError={onError}
        width="100%"
        height={isAudio ? "56px" : "100%"}
      />
    </Suspense>
  );
}

function PreviewFallbackUI(previewFallbackProp: PreviewFallbackProp) {
  const { t } = useTranslation();
  return (
    <div className="flex h-[120px] w-[300px] flex-col items-center justify-center rounded-lg bg-[#4C494C] pt-5 text-white">
      <div className="flex justify-center">{t(`No preview available`)}</div>
      <div className="mt-[20px] flex justify-center">
        <Button
          className="text-[8px]"
          type="button"
          variant="contained"
          size="small"
          sx={{
            borderRadius: 10,
          }}
          onClick={async () => {
            // Share-link context uses fileDownloadUrl (and must refresh the
            // guest cookie before this un-retryable download); the transfer
            // context uses the direct fileUrl. No-op if neither is provided.
            const href =
              previewFallbackProp.fileDownloadUrl ??
              previewFallbackProp.fileUrl?.href;
            if (!href) return;
            if (previewFallbackProp.fileDownloadUrl) {
              // Best-effort refresh; still attempt the download if it fails.
              try {
                await previewFallbackProp.refreshAuth?.();
              } catch {
                // ignore — the download attempt surfaces any auth error itself.
              }
            }
            const anchor = document.createElement("a");
            anchor.href = href;
            anchor.setAttribute("download", previewFallbackProp.fileName);
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
          }}
        >
          <DownloadIcon
            sx={{
              color: "white",
              fontSize: "15px",
              marginRight: "5px",
            }}
          />
          {t(`Download`)}
        </Button>
      </div>
    </div>
  );
}
